// Safety numbers: a 60-digit code two people compare to confirm that the device keys
// their apps are using really belong to each other (defeats a lying server). It covers
// every current device of both users, so it changes whenever either adds or removes one.
import type { UserDevices } from '../api/types'

const CONTEXT = 'hearth-safety:v1:'

function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4)
  const bin = atob(b64)
  return Uint8Array.from(bin, (c) => c.charCodeAt(0))
}

/** 30 digits identifying one user's current set of device keys. */
export async function fingerprint(user: UserDevices): Promise<string> {
  const keys = user.devices.map((d) => d.public_key).sort()
  const parts: Uint8Array[] = [new TextEncoder().encode(`${CONTEXT}${user.user_id}\n`)]
  for (const k of keys) parts.push(b64urlToBytes(k))
  const total = parts.reduce((n, p) => n + p.length, 0)
  const buf = new Uint8Array(new ArrayBuffer(total))
  let off = 0
  for (const p of parts) {
    buf.set(p, off)
    off += p.length
  }
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', buf))
  let digits = ''
  for (let i = 0; i < 6; i++) {
    // 5 bytes → a number < 2^40, reduced to 5 digits.
    let n = 0
    for (let j = 0; j < 5; j++) n = n * 256 + hash[i * 5 + j]!
    digits += String(n % 100000).padStart(5, '0')
  }
  return digits
}

/** The shared safety number for two users (identical on both sides). */
export async function safetyNumber(a: UserDevices, b: UserDevices): Promise<string> {
  const [first, second] = a.user_id < b.user_id ? [a, b] : [b, a]
  return (await fingerprint(first)) + (await fingerprint(second))
}

export function formatSafetyNumber(digits: string): string[] {
  return digits.match(/.{1,5}/g) ?? []
}
